import {
  AGE_TYPES,
  AUDIENCE_TYPE,
  REGION_TYPE,
  DISTRICT_TYPE,
  INTEREST_ACTION_TYPES,
  PLATFORM,
  BRAND_TYPE,
  PRICE_TYPE,
  AUTO_EXTEND_ENABLE,
  INVENTORY_TYPE,
  DEVICE_TYPE_CATEGORY
} from '@/views/promote/targeting/model/toutiao_targeting_consts.js'
import { AD_TYPE, DELIVERY_MODE } from '../../consts/index'
import { getInitForm } from '@/views/promote/targeting/components/toutiao/useToutiaoTargetingForm.js'

/**
 * @description: 通投智选支持的定向
 * @return {*}
 */
export const proceduralTargetings = [
  'audiences',
  'district',
  'city',
  'gender',
  'android_osv',
  'ios_osv',
  'harmony_osv',
  'age',
  'hide_if_converted',
  'hide_if_exists',
  'converted_time_duration',
  'smart_extend',
  'location_type',
  'platform'
]

/**
 * @description: 搜索广告支持的定向
 * @return {*}
 */
export const searchAdTargetings = [
  'auto_extend_enabled',
  'smart_extend',
  'auto_extend_targets',
  'gender',
  'city',
  'device_brand',
  'location_type',
  'platform',
  'superior_popularity_type',
  'activate_type',
  'district',
  'inventory_catalog',
  'audiences',
  'ios_osv',
  'android_osv',
  'harmony_osv',
  'inventory_type',
  'interest_action_mode',
  'hide_if_converted',
  'hide_if_exists',
  'converted_time_duration',
  'age',
  'action_categories',
  'action_words',
  'interest_categories',
  'interest_words'
]

function getInterestActions(params) {
  const actionForm = {}
  const { action_days, action_scene, action_tags, word_tags } = params
  actionForm.action_days = action_days
  actionForm.action_scene = action_scene
  actionForm.action_tags = action_tags
  actionForm.word_tags = word_tags
  // 行为类目词
  actionForm.action_categories = action_tags.filter((tag) => tag.type.toLowerCase() === 'category').map((t) => Number(t.id))
  // 行为关键词
  actionForm.action_words = action_tags.filter((tag) => tag.type.toLowerCase() === 'keyword').map((t) => Number(t.id))
  // 兴趣类目词
  actionForm.interest_categories = word_tags.filter((tsg) => tsg.type.toLowerCase() === 'category').map((t) => Number(t.id))
  // 兴趣关键词
  actionForm.interest_words = word_tags.filter((tsg) => tsg.type.toLowerCase() === 'keyword').map((t) => Number(t.id))
  return actionForm
}

/**
 * @description: 获取定向提交参数
 * @param {*} targetingJson
 * @return {*}
 */
export function getSubmitTargetingParams(targetingJson, { isV3 = false } = {}) {
  const {
    ac,
    age,
    gender,
    inventory_type,
    inventory_catalog,
    audienceType,
    audiences,
    regionType,
    region,
    location_type,
    interest_action_mode,
    platform,
    hide_if_converted,
    hide_if_exists,
    converted_time_duration,
    device_type,
    auto_extend_enabled,
    autotend,
    superior_popularity_type,
    carrier,
    activate_type,
    brand_type,
    brand,
    price_type,
    launch_price
  } = targetingJson

  let targetingParams = {
    inventory_catalog,
    inventory_type,

    gender,
    interest_action_mode,
    hide_if_converted,
    superior_popularity_type
  }

  if (isV3) {
    targetingParams.hide_if_exists = hide_if_exists
    targetingParams.converted_time_duration = converted_time_duration
    targetingParams.smart_extend = auto_extend_enabled
  } else {
    targetingParams.auto_extend_enabled = auto_extend_enabled
  }

  // 人群定向
  const [audienceItem] = Object.values(audiences) || []
  if (audienceType === AUDIENCE_TYPE.AUDIENCE && audienceItem?.accountId) {
    const { accountId, excludes, includes } = audienceItem
    const audiencesParams = {
      account_id: accountId
    }

    if (excludes.length) {
      audiencesParams.retargeting_tags_exclude = excludes
    }
    if (includes.length) {
      audiencesParams.retargeting_tags_include = includes
    }
    if (excludes.length || includes.length) {
      targetingParams.audiences = audiencesParams
    }
  }

  // 地域
  if (regionType === REGION_TYPE.REGION) {
    targetingParams.district = DISTRICT_TYPE.REGION
    targetingParams.city = region
    targetingParams.location_type = location_type
  } else {
    targetingParams.district = DISTRICT_TYPE.NONE
  }

  // 年龄
  const [ageItem] = age
  if (ageItem !== 0) {
    targetingParams.age = age
  }

  // 兴趣行为定向
  if (interest_action_mode === INTEREST_ACTION_TYPES.CUSTOM) {
    targetingParams = { ...targetingParams, ...getInterestActions(targetingJson) }
  }

  // 平台
  if (platform !== PLATFORM.UNLIMITED) {
    targetingParams.platform = [platform]
    const { android_osv, ios_osv, harmony_osv } = targetingJson

    if (platform === PLATFORM.ANDROID) {
      targetingParams.android_osv = android_osv || 'NONE'
    }
    if (platform === PLATFORM.IOS) {
      targetingParams.ios_osv = ios_osv || 'NONE'
    }
    if (platform === PLATFORM.HARMONY) {
      // 鸿蒙时 harmony_osv 有效且必填，兜底 NONE
      targetingParams.harmony_osv = harmony_osv || 'NONE'
    }
  }

  // 设备类型
  const [deviceItem] = device_type || []
  if (typeof deviceItem !== 'undefined' && deviceItem !== 0) {
    targetingParams.device_type = device_type
  }

  // 可放开定向，该字段不为空时表示开启智能放量，传空时表示不开启智能放量允许
  if (auto_extend_enabled === AUTO_EXTEND_ENABLE.ON) {
    targetingParams.auto_extend_targets = autotend
  }

  // 网络类型
  const [acItem] = ac
  if (acItem !== 0) {
    targetingParams.ac = ac
  }

  // 运营商
  const [carrierItem] = carrier
  if (carrierItem !== 0) {
    targetingParams.carrier = carrier
  }

  // 新用户
  const [activateTypeItem] = activate_type
  if (activateTypeItem !== 0) {
    targetingParams.activate_type = activate_type
  }

  // 手机品牌
  if ([PLATFORM.ANDROID, PLATFORM.UNLIMITED].includes(platform)) {
    if (brand_type === BRAND_TYPE.CUSTOM) {
      targetingParams.device_brand = brand
    }
  }

  // 手机价格
  if (price_type === PRICE_TYPE.CUSTOM) {
    targetingParams.launch_price = launch_price
  }
  return targetingParams
}

/**
 * @description: 根据提交的定向参数获取定向组件回显需要的表单结构
 * @description: 适用于头条广告批量复制和修改流程
 * @return {*}
 */
export function rebuildTargetingForm(plan) {
  const targetingParam = plan.targetings.targetingParam || {}
  const { delivery_range, ad_type } = plan.outer_plan_params
  const { union_video_type } = delivery_range || {}
  const {
    audiences,
    city,
    location_type,
    district,
    interest_action_mode,
    platform,
    auto_extend_targets,
    smart_extend,
    device_brand,
    launch_price,
    ...restTargetingParams
  } = targetingParam

  let targetingForm = { ...getInitForm(), ...restTargetingParams }
  // 穿山甲视频创意类型
  if (union_video_type) {
    targetingForm.union_video_type = union_video_type
  }
  if (audiences) {
    const { retargeting_tags_exclude = [], retargeting_tags_include = [] } = audiences

    targetingForm.audiences = {
      // 不能使用audiences内的账号id，要使用当前广告的账号id，因为可能是别的账号推送过来的人群包
      [plan.ad_account_id]: {
        accountId: plan.ad_account_id,
        includes: retargeting_tags_include,
        excludes: retargeting_tags_exclude
      }
    }

    if (retargeting_tags_include.length > 0 || retargeting_tags_exclude.length > 0) {
      targetingForm.audienceType = AUDIENCE_TYPE.AUDIENCE
    }
  }

  if (district === DISTRICT_TYPE.REGION) {
    targetingForm.regionType = REGION_TYPE.REGION
    targetingForm.region = city
    targetingForm.location_type = location_type
  }
  // 兴趣行为定向
  if (targetingParam.interest_action_mode === INTEREST_ACTION_TYPES.CUSTOM) {
    targetingForm = { ...targetingForm, ...getInterestActions(targetingParam) }
  }

  if (platform) {
    const [platformItem] = platform
    targetingForm.platform = platformItem
    const { android_osv, ios_osv, harmony_osv } = targetingParam

    if (platformItem === PLATFORM.ANDROID) {
      targetingForm.android_osv = android_osv || 'NONE'
    }
    if (platformItem === PLATFORM.IOS) {
      targetingForm.ios_osv = ios_osv || 'NONE'
    }
    if (platformItem === PLATFORM.HARMONY) {
      targetingForm.harmony_osv = harmony_osv || 'NONE'
    }
  }

  if (auto_extend_targets) {
    targetingForm.autotend = auto_extend_targets
  }
  if (typeof smart_extend !== 'undefined') {
    targetingForm.auto_extend_enabled = smart_extend
  }
  if (device_brand) {
    targetingForm.brand_type = BRAND_TYPE.CUSTOM
    targetingForm.brand = device_brand
  }

  if (launch_price) {
    targetingForm.price_type = PRICE_TYPE.CUSTOM
    targetingForm.launch_price = launch_price
  }

  if (ad_type === AD_TYPE.SEARCH) {
    targetingForm.inventory_type = [INVENTORY_TYPE.INVENTORY_SEARCH]
  }

  return targetingForm
}

/**
 * @description: 根据媒体后台的参数获取定向组件回显需要的表单
 * @description: 适用于头条单条修改项目
 * @param {*} plan
 * @return {*}
 */
export function rebuildTargetingFormFromMediaParams(audience, delivery_range, accountId) {
  const {
    city,
    district,
    location_type,
    age,
    interest_action_mode,
    action_scene,
    action_days,
    hide_if_converted,
    auto_extend_targets,
    ac,
    carrier,
    activate_type,
    article_category,
    device_brand,
    device_type,
    android_osv,
    launch_price,
    ios_osv,
    harmony_osv,
    retargeting_tags_include,
    retargeting_tags_exclude,
    flow_package,
    exclude_flow_package,
    platform
  } = audience

  const targetingForm = {
    ...getInitForm(),
    ...delivery_range,
    account: {
      id: accountId
    }
  }

  targetingForm.audiences = {
    [accountId]: {
      accountId,
      includes: retargeting_tags_include || [],
      excludes: retargeting_tags_exclude || []
    }
  }
  if (retargeting_tags_include.length > 0 || retargeting_tags_exclude.length > 0) {
    targetingForm.audienceType = AUDIENCE_TYPE.AUDIENCE
  }

  if (district === DISTRICT_TYPE.REGION) {
    targetingForm.regionType = district
    targetingForm.region = city
    targetingForm.location_type = location_type || ''
  }

  if (ac?.length) {
    targetingForm.ac = ac
  }

  if (platform?.length) {
    targetingForm.platform = platform[0]
  }

  if (age?.length) {
    targetingForm.age = age
  }
  targetingForm.interest_action_mode = INTEREST_ACTION_TYPES.CUSTOM
  targetingForm.action_categories = [16]
  targetingForm.action_scene = ['E-COMMERCE']
  targetingForm.action_days = 30

  if (android_osv) {
    targetingForm.android_osv = android_osv
  }

  if (harmony_osv) {
    targetingForm.harmony_osv = harmony_osv
  }

  if (hide_if_converted) {
    targetingForm.hide_if_converted = hide_if_converted
  }

  if (device_type?.length) {
    targetingForm.device_type = device_type
  } else {
  }
  targetingForm.device_type = DEVICE_TYPE_CATEGORY.UNLIMITED

  // if () {

  // }

  return targetingForm
}
